/**
 * First-party attribution capture (alb_* keys) — 30-day first/last touch.
 * Spec: 03-REFERRAL-TRACKING-ARCHITECTURE-DEV-HANDOFF-JULY-11-2026.txt
 */
import { API_BASE_URL } from '../constants';

const COOKIE_DAYS = 30;
const KEYS = {
  anonymousId: 'alb_anonymous_id',
  firstRef: 'alb_first_ref',
  firstUtmSource: 'alb_first_utm_source',
  firstUtmMedium: 'alb_first_utm_medium',
  firstUtmCampaign: 'alb_first_utm_campaign',
  firstUtmContent: 'alb_first_utm_content',
  firstUtmTerm: 'alb_first_utm_term',
  firstLandingUrl: 'alb_first_landing_url',
  firstSeenAt: 'alb_first_seen_at',
  lastRef: 'alb_last_ref',
  lastUtmSource: 'alb_last_utm_source',
  lastUtmMedium: 'alb_last_utm_medium',
  lastUtmCampaign: 'alb_last_utm_campaign',
  lastUtmContent: 'alb_last_utm_content',
  lastUtmTerm: 'alb_last_utm_term',
  lastLandingUrl: 'alb_last_landing_url',
  lastSeenAt: 'alb_last_seen_at',
} as const;

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `alb_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function setItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
  try {
    const maxAge = COOKIE_DAYS * 24 * 60 * 60;
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function getItem(key: string): string | null {
  try {
    const v = localStorage.getItem(key);
    if (v) return v;
  } catch {
    /* ignore */
  }
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${encodeURIComponent(key)}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export type AttributionSnapshot = {
  anonymousId: string;
  referralCode: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  landingUrl: string | null;
  firstTouch: Record<string, string | null>;
  lastTouch: Record<string, string | null>;
};

export function captureAttributionFromUrl(): AttributionSnapshot | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref') || params.get('referral') || params.get('referralCode');
  const utm_source = params.get('utm_source');
  const utm_medium = params.get('utm_medium');
  const utm_campaign = params.get('utm_campaign');
  const utm_content = params.get('utm_content');
  const utm_term = params.get('utm_term');
  const hasAttribution = !!(ref || utm_source || utm_medium || utm_campaign);

  let anonymousId = getItem(KEYS.anonymousId);
  if (!anonymousId) {
    anonymousId = uuid();
    setItem(KEYS.anonymousId, anonymousId);
  }

  const now = new Date().toISOString();
  const landingUrl = window.location.href;

  if (hasAttribution) {
    if (!getItem(KEYS.firstRef) && !getItem(KEYS.firstUtmSource)) {
      setItem(KEYS.firstRef, ref || '');
      setItem(KEYS.firstUtmSource, utm_source || '');
      setItem(KEYS.firstUtmMedium, utm_medium || '');
      setItem(KEYS.firstUtmCampaign, utm_campaign || '');
      setItem(KEYS.firstUtmContent, utm_content || '');
      setItem(KEYS.firstUtmTerm, utm_term || '');
      setItem(KEYS.firstLandingUrl, landingUrl);
      setItem(KEYS.firstSeenAt, now);
    }
    setItem(KEYS.lastRef, ref || getItem(KEYS.lastRef) || '');
    setItem(KEYS.lastUtmSource, utm_source || getItem(KEYS.lastUtmSource) || '');
    setItem(KEYS.lastUtmMedium, utm_medium || getItem(KEYS.lastUtmMedium) || '');
    setItem(KEYS.lastUtmCampaign, utm_campaign || getItem(KEYS.lastUtmCampaign) || '');
    setItem(KEYS.lastUtmContent, utm_content || getItem(KEYS.lastUtmContent) || '');
    setItem(KEYS.lastUtmTerm, utm_term || getItem(KEYS.lastUtmTerm) || '');
    setItem(KEYS.lastLandingUrl, landingUrl);
    setItem(KEYS.lastSeenAt, now);

    // Fire-and-forget click log
    fetch(`${API_BASE_URL}/referrals/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ref,
        referralCode: ref,
        anonymousId,
        landingUrl,
        referrerUrl: document.referrer || null,
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
        utm_term,
      }),
      keepalive: true,
    }).catch(() => {});
  }

  return getAttributionSnapshot();
}

export function getAttributionSnapshot(): AttributionSnapshot {
  const anonymousId = getItem(KEYS.anonymousId) || uuid();
  const firstTouch = {
    ref: getItem(KEYS.firstRef),
    utm_source: getItem(KEYS.firstUtmSource),
    utm_medium: getItem(KEYS.firstUtmMedium),
    utm_campaign: getItem(KEYS.firstUtmCampaign),
    utm_content: getItem(KEYS.firstUtmContent),
    utm_term: getItem(KEYS.firstUtmTerm),
    landingUrl: getItem(KEYS.firstLandingUrl),
    seenAt: getItem(KEYS.firstSeenAt),
  };
  const lastTouch = {
    ref: getItem(KEYS.lastRef) || firstTouch.ref,
    utm_source: getItem(KEYS.lastUtmSource) || firstTouch.utm_source,
    utm_medium: getItem(KEYS.lastUtmMedium) || firstTouch.utm_medium,
    utm_campaign: getItem(KEYS.lastUtmCampaign) || firstTouch.utm_campaign,
    utm_content: getItem(KEYS.lastUtmContent) || firstTouch.utm_content,
    utm_term: getItem(KEYS.lastUtmTerm) || firstTouch.utm_term,
    landingUrl: getItem(KEYS.lastLandingUrl) || firstTouch.landingUrl,
    seenAt: getItem(KEYS.lastSeenAt) || firstTouch.seenAt,
  };

  return {
    anonymousId,
    referralCode: lastTouch.ref || firstTouch.ref,
    utm_source: lastTouch.utm_source,
    utm_medium: lastTouch.utm_medium,
    utm_campaign: lastTouch.utm_campaign,
    utm_content: lastTouch.utm_content,
    utm_term: lastTouch.utm_term,
    landingUrl: lastTouch.landingUrl,
    firstTouch,
    lastTouch,
  };
}

export function attributionForRegisterBody() {
  const snap = getAttributionSnapshot();
  return {
    referralCode: snap.referralCode,
    anonymousId: snap.anonymousId,
    utm_source: snap.utm_source,
    utm_medium: snap.utm_medium,
    utm_campaign: snap.utm_campaign,
    utm_content: snap.utm_content,
    utm_term: snap.utm_term,
    firstTouch: snap.firstTouch,
    lastTouch: snap.lastTouch,
  };
}

export async function syncAttributionToBackend(token?: string | null) {
  const snap = getAttributionSnapshot();
  if (!snap.referralCode && !snap.utm_source) return;
  try {
    await fetch(`${API_BASE_URL}/referrals/attribute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        ref: snap.referralCode,
        referralCode: snap.referralCode,
        anonymousId: snap.anonymousId,
        utm_source: snap.utm_source,
        utm_medium: snap.utm_medium,
        utm_campaign: snap.utm_campaign,
        utm_content: snap.utm_content,
        utm_term: snap.utm_term,
        firstTouch: snap.firstTouch,
        lastTouch: snap.lastTouch,
      }),
    });
  } catch {
    /* ignore */
  }
}
